/**
 * Inline scores card — renders inside the chat message stream
 * showing the top performance scores from recent conversations.
 *
 * Displays up to VISIBLE_COUNT scores sorted ascending (worst first),
 * with an expand button to open the full scores sidebar.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Expand } from 'lucide-react';
import type { RubricOverviewItem } from '@/lib/api/performance';

const VISIBLE_COUNT = 3;

interface Props {
  rubrics: RubricOverviewItem[];
  evalSetName: string | null;
  onExpand: () => void;
}

function scoreTextColor(score: number): string {
  if (score >= 70) return 'text-green-600 dark:text-green-400';
  if (score >= 50) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function scoreIconBg(score: number): string {
  if (score >= 70) return 'bg-green-50 dark:bg-green-950/30 text-green-600 dark:text-green-400';
  if (score >= 50) return 'bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400';
  return 'bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400';
}

const EvalScoresCard: React.FC<Props> = ({ rubrics, evalSetName, onExpand }) => {
  if (rubrics.length === 0) return null;

  // Sort ascending by score (worst first) — show the most actionable items
  const sorted = [...rubrics]
    .filter((r) => r.score_7d != null)
    .sort((a, b) => (a.score_7d ?? 0) - (b.score_7d ?? 0));

  const visible = sorted.slice(0, VISIBLE_COUNT);
  const remaining = sorted.length - VISIBLE_COUNT;
  const headerLabel = evalSetName || 'Performance Scores';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className="rounded-xl border border-border/60 bg-card overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-heading">
            {headerLabel}
          </span>
          <span className="text-xs text-muted-foreground">
            · Recent
          </span>
        </div>
        <button
          type="button"
          onClick={onExpand}
          className="p-1 rounded-md text-muted-foreground hover:text-heading hover:bg-muted/50 transition-colors"
          aria-label="Expand to view all scores"
        >
          <Expand className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Score rows */}
      <div className="divide-y divide-border/30">
        {visible.map((rubric, idx) => {
          const score = rubric.score_7d ?? 0;

          return (
            <motion.div
              key={rubric.criterion_key}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.06, duration: 0.25 }}
              className="flex items-center gap-3 px-4 py-3"
            >
              {/* Score icon */}
              <div
                className={`h-7 w-7 shrink-0 rounded-md flex items-center justify-center ${scoreIconBg(score)}`}
              >
                <BarChart3 className="h-3.5 w-3.5" />
              </div>

              {/* Name */}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-heading leading-tight truncate">
                  {rubric.name}
                </p>
              </div>

              {/* Score value */}
              <span
                className={`text-base font-semibold tabular-nums shrink-0 ${scoreTextColor(score)}`}
              >
                {score.toFixed(1)}
              </span>
            </motion.div>
          );
        })}
      </div>

      {/* View more footer */}
      {remaining > 0 && (
        <button
          type="button"
          onClick={onExpand}
          className="w-full px-4 py-2.5 text-xs text-muted-foreground hover:text-heading hover:bg-muted/30 transition-colors border-t border-border/40 text-left"
        >
          {remaining} more score{remaining !== 1 ? 's' : ''}. Click to see all
        </button>
      )}
    </motion.div>
  );
};

export default EvalScoresCard;
